import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Chip,
  Card,
  CardContent,
  IconButton,
  Collapse
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, FileDownload, FilterList, Clear, Close, PictureAsPdf } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { getInfluencers, getNegotiationCompleted, exportInfluencers, exportFilteredInfluencers } from '../services/api';
import { generateInfluencerPDF } from '../utils/pdfGenerator';
import * as XLSX from 'xlsx';

function Dashboard({ user }) {
  const [influencers, setInfluencers] = useState([]);
  const [filteredInfluencers, setFilteredInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    state: '',
    city: '',
    category: '',
    platformType: '',
    socialMediaPlatforms: '',
    influencerType: '',
    engagementRateInstagram: '',
    engagementRateYoutube: '',
    averageViewInstagram: '',
    averageViewYoutube: '',
    dateFrom: '',
    dateTo: ''
  });
  const [uniqueValues, setUniqueValues] = useState({
    states: [],
    cities: [],
    categories: [],
    platformTypes: [],
    socialMediaPlatforms: [],
    engagementRatesInstagram: [],
    engagementRatesYoutube: [],
    averageViewsInstagram: [],
    averageViewsYoutube: []
  });
  const [genderData, setGenderData] = useState({ series: [], options: {} });
  const [ageData, setAgeData] = useState({ series: [], options: {} });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInfluencers();
  }, []);

  useEffect(() => {
    if (influencers.length > 0) {
      extractUniqueValues();
    }
    applyFilters();
  }, [influencers, filters]);

  const extractUniqueValues = () => {
    if (!Array.isArray(influencers) || influencers.length === 0) return;
    const tableData = user.email == 'admin@hepl.com' ? influencers.filter(i => i.negotiation_status === 1) : influencers;

    const states = [...new Set(tableData.map(i => i.state).filter(Boolean))];
    const cities = [...new Set(tableData.map(i => i.city).filter(Boolean))];
    const categories = [...new Set(tableData.map(i => i.category).filter(Boolean))];
    const platformTypes = [...new Set(tableData.map(i => i.platformType).filter(Boolean))];
    const socialMediaPlatforms = [...new Set(tableData.map(i => i.socialMediaPlatforms).filter(Boolean))];

    // Get actual engagement rates from table data
    const engagementRatesInstagram = [...new Set(tableData.map(i => {
      const rate = calculateEngagementRate(i, 'instagram');
      return rate > 0 ? Math.floor(rate * 10) / 10 : null;
    }).filter(Boolean))].sort((a, b) => a - b);

    const engagementRatesYoutube = [...new Set(tableData.map(i => {
      const rate = calculateEngagementRate(i, 'youtube');
      return rate > 0 ? Math.floor(rate * 10) / 10 : null;
    }).filter(Boolean))].sort((a, b) => a - b);

    // Get actual average views from table data
    const averageViewsInstagram = [...new Set(tableData.map(i => {
      const views = calculateAverageViews(i, 'instagram');
      return views > 0 ? Math.floor(views) : null;
    }).filter(Boolean))].sort((a, b) => a - b);

    const averageViewsYoutube = [...new Set(tableData.map(i => {
      const views = calculateAverageViews(i, 'youtube');
      return views > 0 ? Math.floor(views) : null;
    }).filter(Boolean))].sort((a, b) => a - b);

    setUniqueValues({
      states,
      cities,
      categories,
      platformTypes,
      socialMediaPlatforms,
      engagementRatesInstagram,
      engagementRatesYoutube,
      averageViewsInstagram,
      averageViewsYoutube
    });

    // Gender Distribution - only from table data
    const genderCounts = tableData.reduce((acc, inf) => {
      const gender = inf.gender || 'Unknown';
      acc[gender] = (acc[gender] || 0) + 1;
      return acc;
    }, {});

    const genderSeries = Object.values(genderCounts);
    const genderOptions = {
      labels: Object.keys(genderCounts),
      colors: ['#019383', '#69dc9c', '#f6b042'],
      legend: { position: 'bottom' }
    };

    setGenderData({ series: genderSeries, options: genderOptions });

    // Age Distribution - only from table data
    const ageRanges = {
      '05-17': 0,
      '18-25': 0,
      '26-35': 0,
      '36-45': 0,
      '46+': 0
    };

    tableData.forEach(inf => {
      const age = inf.age;
      if (age >= 5 && age <= 17) ageRanges['05-17']++;
      else if (age >= 18 && age <= 25) ageRanges['18-25']++;
      else if (age >= 26 && age <= 35) ageRanges['26-35']++;
      else if (age >= 36 && age <= 45) ageRanges['36-45']++;
      else if (age >= 46) ageRanges['46+']++;
    });

    const ageSeries = [{
      name: 'Count',
      data: Object.values(ageRanges)
    }];

    const ageOptions = {
      chart: { type: 'bar' },
      xaxis: { categories: Object.keys(ageRanges) },
      colors: ['#019383']
    };

    setAgeData({ series: ageSeries, options: ageOptions });
  };

  const fetchInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getInfluencers();
      setInfluencers(response.data || response);
    } catch (error) {
      console.error('Failed to fetch influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(influencers)) {
      setFilteredInfluencers([]);
      return;
    }

    let filtered = influencers.filter(influencer => {
      // Only show influencers with negotiation_status = 1 and ocr completed.
      if (influencer.ocr_status !== 'completed') return false;

      if (user.email != 'finance@hepl.com' && influencer.negotiation_status !== 1) {
        return false;
      }

      const matchesState = !filters.state || influencer.state === filters.state;
      const matchesCity = !filters.city || influencer.city === filters.city;
      const matchesCategory = !filters.category || influencer.category === filters.category;
      const matchesPlatformType = !filters.platformType || influencer.platformType === filters.platformType;
      const matchesSocialMediaPlatforms = !filters.socialMediaPlatforms ||
        (filters.socialMediaPlatforms === 'Intagram' && influencer.socialMediaPlatforms?.toLowerCase().includes('instagram')) ||
        (filters.socialMediaPlatforms === 'Youtube' && influencer.socialMediaPlatforms?.toLowerCase().includes('youtube'));

      const matchesInfluencerType = !filters.influencerType ||
        influencer.instagramInfluencerType === filters.influencerType ||
        influencer.youtubeInfluencerType === filters.influencerType;

      const instagramEngagement = calculateEngagementRate(influencer, 'instagram');
      const youtubeEngagement = calculateEngagementRate(influencer, 'youtube');
      const instagramViews = calculateAverageViews(influencer, 'instagram');
      const youtubeViews = calculateAverageViews(influencer, 'youtube');

      const matchesInstagramEngagement = !filters.engagementRateInstagram || instagramEngagement >= Number(filters.engagementRateInstagram);
      const matchesYoutubeEngagement = !filters.engagementRateYoutube || youtubeEngagement >= Number(filters.engagementRateYoutube);
      const matchesInstagramViews = !filters.averageViewInstagram || instagramViews >= Number(filters.averageViewInstagram);
      const matchesYoutubeViews = !filters.averageViewYoutube || youtubeViews >= Number(filters.averageViewYoutube);

      const matchesDateFrom = !filters.dateFrom || new Date(influencer.updatedAt) >= new Date(filters.dateFrom);
      const matchesDateTo = !filters.dateTo || new Date(influencer.updatedAt) <= new Date(filters.dateTo + 'T23:59:59');

      return matchesState && matchesCity && matchesCategory && matchesPlatformType &&
        matchesSocialMediaPlatforms && matchesInfluencerType && matchesInstagramEngagement && matchesYoutubeEngagement &&
        matchesInstagramViews && matchesYoutubeViews && matchesDateFrom && matchesDateTo;
    });
    setFilteredInfluencers(filtered);
  };

  const calculateEngagementRate = (influencer, platform) => {
    if (platform === 'instagram') {
      const likes = Number(influencer.instagramLikes) || 0;
      const comments = Number(influencer.instagramComments) || 0;
      const saves = Number(influencer.instagramSaves) || 0;
      const shares = Number(influencer.instagramShares) || 0;
      const reposts = Number(influencer.instagramReposts) || 0;
      const totalReach = Number(influencer.last10ReelsReach);
      if (!totalReach || isNaN(totalReach)) return 0;
      const instagramNoOfPost = Number(influencer.instagramNoOfPost) || 10;
      const avgReach = totalReach / instagramNoOfPost;
      return avgReach === 0 ? 0 : ((likes + comments + saves + shares + reposts) / totalReach) * 100;
    } else {
      const likes = Number(influencer.youtubeLikes) || 0;
      const comments = Number(influencer.youtubeComments) || 0;
      const saves = Number(influencer.youtubeSaves) || 0;
      const shares = Number(influencer.youtubeShares) || 0;
      const totalReach = Number(influencer.last10ShortsReach);
      if (!totalReach || isNaN(totalReach)) return "-";
      let youtubeCount;
      if (influencer.youtubeType === 'Shorts') {
        youtubeCount = Number(influencer.youtubeNoOfShorts) || 10;
      }
      else if (influencer.youtubeType === 'Post') {
        youtubeCount = Number(influencer.youtubeNoOfPost) || 10;
      }
      else {
        return "-";
      }
      const avgReach = totalReach / youtubeCount;
      return avgReach === 0 ? 0 : ((likes + comments + shares) / totalReach) * 100;
    }
  };

  const calculateAverageViews = (influencer, platform) => {
    if (platform === 'instagram') {
      const totalViews = Number(influencer.last10ReelsViews);
      const instagramNoOfPost = Number(influencer?.instagramNoOfPost) || 10;

      if (isNaN(totalViews) || instagramNoOfPost === 0) return 0;

      return Number((totalViews / instagramNoOfPost).toFixed(2));
    } else {
      let totalViews = Number(influencer.last10ShortsViews);
      let youtubeCount;
      if (influencer.youtubeType === 'Shorts') {
        youtubeCount = Number(influencer.youtubeNoOfShorts) || 10;
      }
      else if (influencer.youtubeType === 'Post') {
        youtubeCount = Number(influencer.youtubeNoOfPost) || 10;
      }
      else {
        return "-";
      }

      if (isNaN(totalViews) || youtubeCount === 0) return 0;

      return Number((totalViews / youtubeCount).toFixed(2));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      state: '',
      city: '',
      category: '',
      platformType: '',
      socialMediaPlatforms: '',
      influencerType: '',
      engagementRateInstagram: '',
      engagementRateYoutube: '',
      averageViewInstagram: '',
      averageViewYoutube: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const columns = [
    { field: 'sno', headerName: 'SNO', width: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 150 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'onboarderName', headerName: 'Onboarder', width: 150 },
    { field: 'instagramLink', headerName: 'Profile Link (IN)', width: 180 },
    { field: 'youtubeLink', headerName: 'Profile Link (YT)', width: 180 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'category', headerName: 'Category', width: 150 },
    // { field: 'platformType', headerName: 'Platform Type', width: 130 },
    { field: 'socialMediaPlatforms', headerName: 'Platform Type', width: 180 },
    { field: 'number', headerName: 'Number', width: 180 },
    { field: 'address', headerName: 'Address', width: 180 },
    { field: 'panNumber', headerName: 'PAN Number', width: 150 },
    { field: 'aadharNumber', headerName: 'Aadhar Number', width: 150 },
    { field: 'bankAccountNumber', headerName: 'Bank Account', width: 150 },
    { field: 'ifscCode', headerName: 'IFSC Code', width: 120 },
    { field: 'holderName', headerName: 'Holder Name', width: 150 },
    { field: 'bankName', headerName: 'Bank Name', width: 150 },
    {
      field: 'instagramInfluencerType',
      headerName: 'Instagram Type',
      width: 180,
      // cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const type = params.row.allData?.instagramInfluencerType;
        if (!type || type === '-') return '-';
        const getColor = (type) => {
          switch (type) {
            case 'Micro': return 'primary';
            case 'Macro': return 'secondary';
            case 'Mid-tier': return 'success';
            case 'Mega': return 'warning';
            case 'Celeb': return 'error';
            default: return 'default';
          }
        };
        return <Chip label={type} color={getColor(type)} size="small" />;
      }
    },
    {
      field: 'youtubeInfluencerType',
      headerName: 'YouTube Subscriber Type',
      width: 180,
      // cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const type = params.row.allData?.youtubeInfluencerType;
        if (!type || type === '-') return '-';
        const getColor = (type) => {
          switch (type) {
            case 'Micro': return 'primary';
            case 'Macro': return 'secondary';
            case 'Mid-tier': return 'success';
            case 'Mega': return 'warning';
            case 'Celeb': return 'error';
            default: return 'default';
          }
        };
        return <Chip label={type} color={getColor(type)} size="small" />;
      }
    },
    {
      field: 'totalCostInstagram',
      headerName: 'Total Cost (Instagram)',
      width: 180,
      cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const reelCost = params.row.allData?.reelCost || 0;
        const storyCost = params.row.allData?.storyCost || 0;
        const total = reelCost + storyCost;
        return total > 0 ? `₹${total.toLocaleString()}` : '-';
      }
    },
    {
      field: 'negotiationCostInstagram',
      headerName: 'Negotiation Total Cost (Instagram)',
      width: 220,
      cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const negotiationCost = params.row.allData?.negotiationCost?.reelCost || 0;
        return negotiationCost > 0 ? `₹${negotiationCost.toLocaleString()}` : '-';
      }
    },
    {
      field: 'engagementRateInstagram',
      headerName: 'Engagement Rate (Instagram)',
      width: 200,
      cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const rate = calculateEngagementRate(params.row.allData, 'instagram');
        return rate > 0 ? Number(rate.toFixed(2)) : '-';
      }
    },
    {
      field: 'averageViewInstagram',
      headerName: 'Average View (Instagram)',
      width: 180,
      cellClassName: 'instagram-cell',
      renderCell: (params) => {
        const views = calculateAverageViews(params.row.allData, 'instagram');
        return views > 0 ? views.toLocaleString() : '-';
      }
    },
    {
      field: 'last10ReelsViews',
      headerName: 'Instagram Views',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'last10ReelsReach',
      headerName: 'Instagram Reach',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'instagramLikes',
      headerName: 'Instagram Likes',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'instagramComments',
      headerName: 'Instagram Comments',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'instagramSaves',
      headerName: 'Instagram Saves',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'instagramShares',
      headerName: 'Instagram Shares',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'instagramReposts',
      headerName: 'Instagram Reposts',
      width: 150,
      cellClassName: 'instagram-cell',
    },
    {
      field: 'youtubeType',
      headerName: 'YouTube Type',
      width: 150,
      headerClassName: 'youtube-header',
      cellClassName: 'youtube-cell',
    },
    {
      field: 'totalCostYoutube',
      headerName: 'Total Cost (YouTube)',
      width: 180,
      cellClassName: 'youtube-cell',
      renderCell: (params) => {
        let val;
        if (params.row.allData?.youtubeType == 'Shorts') {
          val = params.row.allData?.youtubeShortsCost;
        } else {
          val = params.row.allData?.youtubeVideoCost;
        }
        if (!val) return "-";
        return `₹${val.toLocaleString()}`;
      }
    },
    {
      field: 'negotiationCostYoutube',
      headerName: 'Negotiation Total Cost (Youtube)',
      width: 220,
      cellClassName: 'youtube-cell',
      renderCell: (params) => {
        let val;
        if (params.row.allData?.youtubeType == 'Shorts') {
          val = params.row.allData?.negotiationCost?.youtubeShortsCoast;
        } else {
          val = params.row.allData?.negotiationCost?.youtubeVideoCost;
        }
        if (!val) return "-";
        return val > 0 ? `₹${val.toLocaleString()}` : '-';
      }
    },
    {
      field: 'engagementRateYoutube',
      headerName: 'Engagement Rate (Youtube)',
      width: 200,
      cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const rate = calculateEngagementRate(params.row.allData, 'youtube');
        return rate > 0 ? Number(rate.toFixed(2)) : '-';
      }
    },
    {
      field: 'averageViewYoutube',
      headerName: 'Average View (Youtube)',
      width: 180,
      cellClassName: 'youtube-cell',
      renderCell: (params) => {
        const views = calculateAverageViews(params.row.allData, 'youtube');
        return views > 0 ? views.toLocaleString() : '-';
      }
    },
    {
      field: 'last10ShortsViews',
      headerName: 'Instagram Views',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'last10ShortsReach',
      headerName: 'Instagram Reach',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'youtubeLikes',
      headerName: 'Youtube Likes',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'youtubeComments',
      headerName: 'Youtube Comments',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'youtubeSaves',
      headerName: 'Youtube Saves',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'youtubeShares',
      headerName: 'Youtube Shares',
      width: 150,
      cellClassName: 'youtube-cell',
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 180,
      valueGetter: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const h = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const hours = h % 12 || 12;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/influencer/${params.row._id}`)}
            sx={{ color: '#019383' }}
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => generatePDF(params.row.allData)}
            sx={{ color: '#019383' }}
          >
            <PictureAsPdf />
          </IconButton>
        </Box>
      )
    }
  ];

  const rows = filteredInfluencers.map((influencer, index) => ({
    id: influencer._id,
    sno: index + 1,
    influencerCode: influencer.influencerCode,
    name: influencer.name,
    updatedAt: influencer.updatedAt,
    state: influencer.state,
    city: influencer.city,
    category: influencer.category,
    platformType: influencer.platformType,
    socialMediaPlatforms: influencer.socialMediaPlatforms,
    number: influencer.contactNumber,
    address: influencer.address,
    panNumber: influencer.panNumber,
    aadharNumber: influencer.aadharNumber,
    last10ReelsViews: influencer.last10ReelsViews,
    last10ReelsReach: influencer.last10ReelsReach,
    instagramLikes: influencer.instagramLikes,
    instagramComments: influencer.instagramComments,
    instagramSaves: influencer.instagramSaves,
    instagramShares: influencer.instagramShares,
    instagramReposts: influencer.instagramReposts,
    last10ShortsViews: influencer.last10ShortsViews,
    last10ShortsReach: influencer.last10ShortsReach,
    youtubeType: influencer.youtubeType,
    youtubeLikes: influencer.youtubeLikes,
    youtubeComments: influencer.youtubeComments,
    youtubeSaves: influencer.youtubeSaves,
    youtubeShares: influencer.youtubeShares,
    bankAccountNumber: influencer.bankAccountNumber,
    ifscCode: influencer.ifscCode,
    holderName: influencer.holderName,
    bankName: influencer.bankName,
    onboarderName: influencer.onboarderId.name,
    _id: influencer._id,
    allData: influencer,
    instagramLink: influencer.instagramLink ?? '-',
    youtubeLink: influencer.youtubeLink ?? '-',
  }));

  const generatePDF = (influencer) => {
    generateInfluencerPDF(influencer, 'details');
  };

  const exportToExcel = () => {
    try {
      const data = filteredInfluencers.map((influencer, index) => {
        const igEngagement = calculateEngagementRate(influencer, 'instagram');
        const ytEngagement = calculateEngagementRate(influencer, 'youtube');
        const igAvgView = calculateAverageViews(influencer, 'instagram');
        const ytAvgView = calculateAverageViews(influencer, 'youtube');
        const igTotalCost = (influencer.reelCost || 0) + (influencer.storyCost || 0);
        let ytTotalCost;
        if (influencer.youtubeType === 'Shorts') {
          ytTotalCost = influencer.youtubeShortsCost || 0;
        } else {
          ytTotalCost = influencer.youtubeVideoCost || 0;
        }
        let ytNegCost;
        if (influencer.youtubeType === 'Shorts') {
          ytNegCost = influencer.negotiationCost?.youtubeShortsCoast || 0;
        } else {
          ytNegCost = influencer.negotiationCost?.youtubeVideoCost || 0;
        }

        return {
          'SNO': index + 1,
          'Name': influencer.name || '',
          'Onboarder': influencer.onboarderId?.name || '',
          'Profile Link (Instagram)': influencer.instagramLink || '-',
          'Profile Link (YouTube)': influencer.youtubeLink || '-',
          'State': influencer.state || '',
          'City': influencer.city || '',
          'Category': influencer.category || '',
          'Platform Type': influencer.socialMediaPlatforms || '',
          'Number': influencer.contactNumber || '',
          'Address': influencer.address || '',
          'PAN Number': influencer.panNumber || '',
          'Aadhar Number': influencer.aadharNumber || '',
          'Bank Account': influencer.bankAccountNumber || '',
          'IFSC Code': influencer.ifscCode || '',
          'Holder Name': influencer.holderName || '',
          'Bank Name': influencer.bankName || '',
          'Instagram Type': influencer.instagramInfluencerType || '-',
          'YouTube Subscriber Type': influencer.youtubeInfluencerType || '-',
          'Total Cost (Instagram)': igTotalCost > 0 ? igTotalCost : '-',
          'Negotiation Total Cost (Instagram)': influencer.negotiationCost?.reelCost || '-',
          'Engagement Rate (Instagram)': igEngagement > 0 ? Number(igEngagement.toFixed(2)) : '-',
          'Average View (Instagram)': igAvgView > 0 ? Math.floor(igAvgView) : '-',
          'Instagram Views': influencer.last10ReelsViews || '-',
          'Instagram Reach': influencer.last10ReelsReach || '-',
          'Instagram Likes': influencer.instagramLikes || '-',
          'Instagram Comments': influencer.instagramComments || '-',
          'Instagram Saves': influencer.instagramSaves || '-',
          'Instagram Shares': influencer.instagramShares || '-',
          'Instagram Reposts': influencer.instagramReposts || '-',
          'YouTube Type': influencer.youtubeType || '-',
          'Total Cost (YouTube)': ytTotalCost > 0 ? ytTotalCost : '-',
          'Negotiation Total Cost (YouTube)': ytNegCost > 0 ? ytNegCost : '-',
          'Engagement Rate (YouTube)': (ytEngagement !== '-' && ytEngagement > 0) ? Number(ytEngagement.toFixed(2)) : '-',
          'Average View (YouTube)': (ytAvgView !== '-' && ytAvgView > 0) ? Math.floor(ytAvgView) : '-',
          'YouTube Views': influencer.last10ShortsViews || '-',
          'YouTube Reach': influencer.last10ShortsReach || '-',
          'YouTube Likes': influencer.youtubeLikes || '-',
          'YouTube Comments': influencer.youtubeComments || '-',
          'YouTube Saves': influencer.youtubeSaves || '-',
          'YouTube Shares': influencer.youtubeShares || '-',
          'Updated At': influencer.updatedAt ? new Date(influencer.updatedAt).toLocaleDateString('en-GB') : '-',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dashboard Data');
      XLSX.writeFile(workbook, 'dashboard_data.xlsx');
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
          Dashboard Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            color="success"
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Filters</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* <Button
                  startIcon={<Clear />}
                  onClick={clearAllFilters}
                  size="small"
                  sx={{ color: '#019383' }}
                >
                  Clear All
                </Button> */}
                <IconButton
                  onClick={() => setShowFilters(false)}
                // sx={{ color: '#019383' }}
                >
                  <Close />
                </IconButton>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="State"
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.states.map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="City"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.cities.map(city => (
                    <MenuItem key={city} value={city}>{city}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.categories.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Platform Type"
                  value={filters.socialMediaPlatforms}
                  onChange={(e) => handleFilterChange('socialMediaPlatforms', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Intagram">Instagram</MenuItem>
                  <MenuItem value="Youtube">Youtube</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Influencer Type"
                  value={filters.influencerType}
                  onChange={(e) => handleFilterChange('influencerType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Micro">Micro: 10K-100K</MenuItem>
                  <MenuItem value="Macro">Macro: 100K-500K</MenuItem>
                  <MenuItem value="Mid-tier">Mid-tier: 500K-1M</MenuItem>
                  <MenuItem value="Mega">Mega: 1M-5M</MenuItem>
                  <MenuItem value="Celeb">Celeb: 5M+</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Engagement Rate (Instagram)"
                  value={filters.engagementRateInstagram}
                  onChange={(e) => handleFilterChange('engagementRateInstagram', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.engagementRatesInstagram.map(rate => (
                    <MenuItem key={rate} value={rate}>{rate}%+</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Engagement Rate (Youtube)"
                  value={filters.engagementRateYoutube}
                  onChange={(e) => handleFilterChange('engagementRateYoutube', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.engagementRatesYoutube.map(rate => (
                    <MenuItem key={rate} value={rate}>{rate}%+</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Average View (Instagram)"
                  value={filters.averageViewInstagram}
                  onChange={(e) => handleFilterChange('averageViewInstagram', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.averageViewsInstagram.map(views => (
                    <MenuItem key={views} value={views}>{views.toLocaleString()}+</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Average View (Youtube)"
                  value={filters.averageViewYoutube}
                  onChange={(e) => handleFilterChange('averageViewYoutube', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {uniqueValues.averageViewsYoutube.map(views => (
                    <MenuItem key={views} value={views}>{views.toLocaleString()}+</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date From"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date To"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <DataGrid
            columns={columns}
            rows={rows}
            loading={loading}
            // initialState={{
            //   pagination: {
            //     paginationModel: { page: 0, pageSize: 10 },
            //   },
            // }}
            pageSize={10}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa'
              },
              '& .instagram-cell': {
                backgroundColor: '#e8f4fcb7 !important'
              },
              '& .youtube-cell': {
                backgroundColor: '#fff5e69a !important'
              }
            }}
          />
          {/* <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            loading={loading}
            autoHeight
            checkboxSelection
            getRowId={(row) => row.id}
          /> */}
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={4.8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Gender Distribution</Typography>
              <ReactApexChart
                options={genderData.options}
                series={genderData.series}
                type="pie"
                width="100%"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7.2}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Age Distribution</Typography>
              <ReactApexChart
                options={ageData.options}
                series={ageData.series}
                type="bar"
                height={380}
                width="100%"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;